import React, { useCallback, useState, useRef } from 'react';
import { Container, Box, CircularProgress, Typography } from '@mui/material';
import CostCalculator from '../components/CostCalculator';
import AppToolbar from '../components/AppToolbar';
import GlobalSettingsDialog from '../components/calculator/modals/GlobalSettingsDialog';
import FMCRegionWarningDialog from '../components/calculator/modals/FMCRegionWarningDialog';
import { CalculatorProvider } from '../contexts/CalculatorContext';
import { useTabs } from '../contexts/TabsContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

export default function CalculatorPage({ onOpenProfile }) {
  const { activeTab, updateActiveTabData, loading, error } = useTabs();
  const {
    language,
    region,
    setLanguage,
    setRegion,
    loading: prefsLoading
  } = useUserPreferences();

  // Global settings dialog state (lifted here because AppToolbar is outside CalculatorProvider)
  const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);

  // FMC region warning dialog state
  const [fmcWarningOpen, setFmcWarningOpen] = useState(false);
  const pendingRegionRef = useRef(null);
  const calculatorContextRef = useRef(null);

  const handleStateSave = useCallback((newState) => {
    updateActiveTabData(newState);
  }, [updateActiveTabData]);

  const handleRegionChangeRequest = useCallback((currentRegion, newRegion) => {
    // Check if we're changing from Ukraine to non-Ukraine region
    const isFromUkraine = currentRegion === 'ua' || 
                         String(currentRegion).toLowerCase().includes('ukr') || 
                         String(currentRegion).toLowerCase().includes('ua') || 
                         String(currentRegion).toLowerCase() === 'ukraine';
    
    const isToNonUkraine = !(newRegion === 'ua' || 
                            String(newRegion).toLowerCase().includes('ukr') || 
                            String(newRegion).toLowerCase().includes('ua') || 
                            String(newRegion).toLowerCase() === 'ukraine');

    // Check if FMC is selected in active tab
    const tabData = activeTab?.settings || {};
    const businessPhoneType = tabData.businessPhoneType || 'business-phone';
    const fmcUsers = tabData.fmcUsers || 0;
    const hasFMC = (businessPhoneType === 'fmc' || businessPhoneType === 'business-phone-fmc') && fmcUsers > 0;

    if (isFromUkraine && isToNonUkraine && hasFMC) {
      // Show warning dialog
      pendingRegionRef.current = newRegion;
      setFmcWarningOpen(true);
    } else {
      // Proceed with region change
      setRegion(newRegion);
    }
  }, [activeTab, setRegion]);

  const handleFmcWarningConfirm = useCallback(() => {
    setFmcWarningOpen(false);
    if (pendingRegionRef.current) {
      // Reset FMC settings before changing region
      const currentTabData = activeTab?.settings || {};
      const updatedSettings = {
        ...currentTabData,
        businessPhoneType: 'business-phone', // Reset to business phone only
        fmcUsers: 0 // Reset FMC users to 0
      };
      
      // Update the tab data to reset FMC settings
      updateActiveTabData(updatedSettings);
      
      // Change the region
      setRegion(pendingRegionRef.current);
      pendingRegionRef.current = null;
    }
  }, [setRegion, activeTab, updateActiveTabData]);

  const handleFmcWarningCancel = useCallback(() => {
    setFmcWarningOpen(false);
    pendingRegionRef.current = null;
  }, []);

  const renderContent = () => {
    if (loading || prefsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <Typography color="error">Failed to load tabs: {error.message}</Typography>
        </Box>
      );
    }

    if (!activeTab) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <Typography>No active tabs. Create a new one to start.</Typography>
        </Box>
      );
    }

    return (
      <CalculatorProvider
        key={activeTab.id} // Ensure provider re-mounts on tab change
        instanceTabId={activeTab.id}
        tabData={activeTab.settings || {}}
        onStateSave={handleStateSave}
      >
        <Container maxWidth="xl" sx={{ pt: 4, pb: 4 }}>
          <CostCalculator />
        </Container>
        <GlobalSettingsDialog open={globalSettingsOpen} onClose={() => setGlobalSettingsOpen(false)} />
      </CalculatorProvider>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7fafb' }}>
      <AppToolbar
        region={region}
        setRegion={setRegion}
        language={language}
        setLanguage={setLanguage}
        onOpenProfile={onOpenProfile}
        // These props might be needed later for global settings
        hasGlobalSettings={
          localStorage.getItem('showAdvancedTier') === 'true' ||
          localStorage.getItem('showPricesWithoutBonuses') === 'true'
        }
        onOpenGlobalSettings={() => setGlobalSettingsOpen(true)}
        onRegionChangeRequest={handleRegionChangeRequest}
      />
      {renderContent()}
      <FMCRegionWarningDialog 
        open={fmcWarningOpen}
        onConfirm={handleFmcWarningConfirm}
        onCancel={handleFmcWarningCancel}
      />
    </Box>
  );
}
